import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Icon, V2Badge } from '@sakhtar/design-system/react';
import { faDigits } from '../../lib/faDigits.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { NORMAL_BALANCE_LABEL } from '../../lib/types/finance.js';
import type { CoaBalance, CoaBalancesState } from './CoaBalances.js';
import { LEVEL_LABELS, splitCode, type TreeNode } from './CoaTree.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html, frame `/t/kimia/finance`,
 * «کدینگ حساب‌ها» card — indented tree grid (حساب / سطح / ماهیت / مانده
 * بدهکار / مانده بستانکار) with a sticky «جمع مانده‌ها — تراز آزمایشی» totals
 * row. Extra column «ثبت سند» keeps the real set-postable action the
 * reference doesn't show (PATCH .../finance/accounts/:code).
 */

const GRID_COLUMNS = 'minmax(360px,3fr) 96px 96px 112px minmax(150px,1fr) minmax(150px,1fr)';
const MOD_SOLID = 'color-mix(in oklch, var(--sk-mod-c) var(--sk-mod-k), #0a0c10)';
const MOD_TEXT = 'color-mix(in oklch, var(--sk-mod-c) 60%, var(--sk-fg))';

const CELL: CSSProperties = {
  padding: '0 12px',
  borderBottom: '1px solid var(--sk-divider)',
  display: 'flex',
  alignItems: 'center',
  minHeight: 34,
  minWidth: 0,
};

const HEAD_CELL: CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid var(--sk-border)',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
};

const CHIP_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  padding: '0 8px',
  borderRadius: 6,
  boxSizing: 'border-box',
  fontSize: 11,
  fontWeight: 700,
};

/** Level chip: solid → tinted → outlined → sunken as the tree gets deeper (never colour alone — the label is text). */
export function levelChipStyle(depth: number): CSSProperties {
  switch (Math.min(depth, LEVEL_LABELS.length - 1)) {
    case 0:
      return {
        ...CHIP_BASE,
        color: '#fff',
        background: MOD_SOLID,
        border: '1px solid transparent',
      };
    case 1:
      return {
        ...CHIP_BASE,
        color: MOD_TEXT,
        background: 'color-mix(in oklch, var(--sk-mod-c) 18%, var(--sk-bg-elev))',
        border: '1px solid transparent',
      };
    case 2:
      return {
        ...CHIP_BASE,
        color: MOD_TEXT,
        background: 'transparent',
        border: '1px solid color-mix(in oklch, var(--sk-mod-c) 45%, transparent)',
      };
    default:
      return {
        ...CHIP_BASE,
        color: 'var(--sk-fg-muted)',
        background: 'var(--sk-bg-sunken)',
        border: '1px solid var(--sk-border)',
      };
  }
}

/** The legend shown in the toolbar — same chips as the rows. */
export function CoaLevelLegend() {
  return (
    <div
      style={{
        marginInlineStart: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {LEVEL_LABELS.map((label, depth) => (
        <span key={label} style={levelChipStyle(depth)}>
          {label}
        </span>
      ))}
    </div>
  );
}

function amountCell(
  balances: CoaBalancesState,
  balance: CoaBalance | undefined,
  side: 'debitRial' | 'creditRial',
  unit: AmountDisplayUnit,
): string {
  if (balances.status === 'loading') return '…';
  if (balances.status === 'error') return '—';
  const digits = balance?.[side] ?? '0';
  return BigInt(digits) > 0n ? formatRialFa(digits, unit) : '—';
}

export function CoaTreeGrid({
  rows,
  codeByAccountId,
  expanded,
  onToggle,
  balances,
  unit,
  base,
  postableBusyRef,
  onTogglePostable,
}: {
  rows: readonly TreeNode[];
  codeByAccountId: ReadonlyMap<string, string>;
  expanded: ReadonlySet<string>;
  onToggle: (accountId: string) => void;
  balances: CoaBalancesState;
  unit: AmountDisplayUnit;
  base: string;
  postableBusyRef: string | null;
  onTogglePostable: (row: TreeNode) => void;
}) {
  return (
    <div className="sk-table-scroll" style={{ maxHeight: 620 }}>
      <div
        role="table"
        aria-label="کدینگ حساب‌ها"
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          minWidth: 960,
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--sk-fg)',
        }}
      >
        <div
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: '1 / -1',
            position: 'sticky',
            top: 0,
            zIndex: 3,
            background: 'color-mix(in oklch, var(--sk-mod-c) 8%, var(--sk-bg-elev))',
            fontWeight: 500,
            color: 'var(--sk-fg-muted)',
            fontSize: 12,
          }}
        >
          <div role="columnheader" style={HEAD_CELL}>
            حساب
          </div>
          <div role="columnheader" style={HEAD_CELL}>
            سطح
          </div>
          <div role="columnheader" style={HEAD_CELL}>
            ماهیت
          </div>
          <div role="columnheader" style={HEAD_CELL}>
            ثبت سند
          </div>
          <div role="columnheader" style={{ ...HEAD_CELL, justifyContent: 'flex-end' }}>
            مانده بدهکار
          </div>
          <div role="columnheader" style={{ ...HEAD_CELL, justifyContent: 'flex-end' }}>
            مانده بستانکار
          </div>
        </div>

        {rows.map((row) => {
          const isOpen = expanded.has(row.accountId);
          const hasKids = row.children.length > 0;
          const [prefix, own] = splitCode(
            row.code,
            row.parentAccountId ? codeByAccountId.get(row.parentAccountId) : undefined,
          );
          const balance = balances.status === 'ready' ? balances.byCode.get(row.code) : undefined;
          return (
            <div
              key={row.accountId}
              role="row"
              style={{
                display: 'grid',
                gridTemplateColumns: 'subgrid',
                gridColumn: '1 / -1',
                background:
                  row.depth === 0
                    ? 'color-mix(in oklch, var(--sk-mod-c) 5%, var(--sk-bg-elev))'
                    : undefined,
              }}
            >
              <div role="cell" style={{ ...CELL, gap: 6 }}>
                <span
                  aria-hidden="true"
                  style={{
                    alignSelf: 'stretch',
                    flex: '0 0 auto',
                    width: row.depth * 18,
                    background:
                      'repeating-linear-gradient(to left, transparent 0 8px, color-mix(in oklch, var(--sk-mod-c) 38%, transparent) 8px 9px, transparent 9px 18px)',
                  }}
                />
                {hasKids ? (
                  <button
                    type="button"
                    className={isOpen ? 'sk-tree-toggle is-open' : 'sk-tree-toggle'}
                    onClick={() => onToggle(row.accountId)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'جمع کردن' : 'باز کردن'}
                  >
                    <Icon name="chevronLeft" size="sm" />
                  </button>
                ) : (
                  <span className="sk-tree-spacer" />
                )}
                <Link
                  to={`${base}/finance/accounts/${row.accountId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    color: 'inherit',
                    textDecoration: 'none',
                  }}
                >
                  <span
                    className="sk-num"
                    style={{
                      direction: 'ltr',
                      unicodeBidi: 'isolate',
                      fontSize: 12.5,
                      letterSpacing: '.02em',
                    }}
                  >
                    <span style={{ color: 'var(--sk-fg-subtle)' }}>{prefix}</span>
                    <span
                      style={{ fontWeight: 800, color: row.depth === 0 ? MOD_TEXT : undefined }}
                    >
                      {own}
                    </span>
                  </span>
                  <span
                    style={{
                      fontWeight: row.depth === 0 ? 700 : row.depth === 1 ? 600 : 400,
                      fontSize: row.depth === 0 ? 13.5 : 13,
                    }}
                  >
                    {row.name}
                  </span>
                </Link>
                {hasKids ? (
                  <span
                    className="sk-num"
                    title="تعداد زیرحساب مستقیم"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--sk-fg-muted)',
                      padding: '1px 7px',
                      borderRadius: 999,
                      background: 'var(--sk-bg-sunken)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {faDigits(row.children.length)}
                  </span>
                ) : null}
                {row.status !== 'active' ? <V2Badge tone="neutral">غیرفعال</V2Badge> : null}
              </div>
              <div role="cell" style={CELL}>
                <span style={levelChipStyle(row.depth)}>
                  {LEVEL_LABELS[Math.min(row.depth, LEVEL_LABELS.length - 1)]}
                </span>
              </div>
              <div role="cell" style={{ ...CELL, fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                {row.normalBalance ? NORMAL_BALANCE_LABEL[row.normalBalance] : '—'}
              </div>
              <div role="cell" style={CELL}>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  disabled={postableBusyRef === row.code}
                  onClick={() => onTogglePostable(row)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    padding: '0 6px',
                    color: row.isPostable ? 'var(--sk-success)' : 'var(--sk-fg-subtle)',
                  }}
                >
                  {row.isPostable ? <Icon name="check" size="sm" /> : null}
                  {row.isPostable ? 'قابل ثبت' : 'غیرقابل ثبت'}
                </button>
              </div>
              <div
                role="cell"
                className="sk-num"
                style={{ ...CELL, justifyContent: 'flex-end', fontWeight: hasKids ? 600 : 400 }}
              >
                {amountCell(balances, balance, 'debitRial', unit)}
              </div>
              <div
                role="cell"
                className="sk-num"
                style={{ ...CELL, justifyContent: 'flex-end', fontWeight: hasKids ? 600 : 400 }}
              >
                {amountCell(balances, balance, 'creditRial', unit)}
              </div>
            </div>
          );
        })}

        {balances.status === 'ready' ? (
          <div
            role="row"
            style={{
              display: 'grid',
              gridTemplateColumns: 'subgrid',
              gridColumn: '1 / -1',
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              background: 'color-mix(in oklch, var(--sk-mod-c) 14%, var(--sk-bg-elev))',
              fontWeight: 700,
              boxShadow: 'inset 0 2px 0 color-mix(in oklch, var(--sk-mod-c) 55%, transparent)',
            }}
          >
            <div role="cell" style={{ ...HEAD_CELL, borderBottom: 0, color: 'var(--sk-fg)' }}>
              جمع مانده‌ها — تراز آزمایشی
            </div>
            <div role="cell" style={{ ...HEAD_CELL, borderBottom: 0 }} />
            <div role="cell" style={{ ...HEAD_CELL, borderBottom: 0 }}>
              <V2Badge tone={balances.balanced ? 'success' : 'danger'}>
                {balances.balanced ? 'تراز است' : 'تراز نیست'}
              </V2Badge>
            </div>
            <div role="cell" style={{ ...HEAD_CELL, borderBottom: 0 }} />
            <div
              role="cell"
              className="sk-num"
              style={{ ...HEAD_CELL, borderBottom: 0, justifyContent: 'flex-end' }}
            >
              {formatRialFa(balances.totals.debitRial, unit)}
            </div>
            <div
              role="cell"
              className="sk-num"
              style={{ ...HEAD_CELL, borderBottom: 0, justifyContent: 'flex-end' }}
            >
              {formatRialFa(balances.totals.creditRial, unit)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
